import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, Send, Users, Mail, Building2, User, Clock, CheckCircle, 
  XCircle, AlertCircle, RefreshCw, Trash2, Plus
} from 'lucide-react';

const References = () => {
  const [loading, setLoading] = useState(true);
  const [references, setReferences] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    referee_name: '',
    referee_email: '',
    referee_relationship: '',
    referee_company: '',
    custom_message: ''
  });

  const relationships = [
    'Direct Manager',
    'Skip-Level Manager',
    'Colleague/Peer',
    'Direct Report',
    'HR Representative',
    'Client/Customer',
    'Mentor',
    'Business Partner',
    'Professor/Teacher',
    'Other'
  ];

  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      const res = await employeeAPI.getReferenceRequests();
      setReferences(res.data);
    } catch (error) {
      toast.error('Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.referee_name || !formData.referee_email || !formData.referee_relationship) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.referee_email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await employeeAPI.createReferenceRequest(formData);
      toast.success(`Reference request sent to ${formData.referee_email}`);
      setShowModal(false);
      setFormData({
        referee_name: '',
        referee_email: '',
        referee_relationship: '',
        referee_company: '',
        custom_message: ''
      });
      fetchReferences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reference request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id) => {
    try {
      await employeeAPI.resendReferenceRequest(id);
      toast.success('Reminder sent');
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleCancel = async (id) => {
    try {
      await employeeAPI.cancelReferenceRequest(id);
      toast.success('Request cancelled');
      fetchReferences();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'text-amber-400 bg-amber-500/20 border-amber-500/30', label: 'Pending' },
      completed: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20 border-green-500/30', label: 'Completed' },
      declined: { icon: XCircle, color: 'text-red-400 bg-red-500/20 border-red-500/30', label: 'Declined' },
      expired: { icon: AlertCircle, color: 'text-[#5B6472] bg-[#F5F8FF] border-[#E8EDF5]', label: 'Expired' },
      cancelled: { icon: XCircle, color: 'text-[#5B6472] bg-[#F5F8FF] border-[#E8EDF5]', label: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="references-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">My References</h1>
            <p className="mt-1 text-[#5B6472] text-sm">
              Request references from colleagues, managers, and professional contacts.
            </p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            data-testid="request-reference-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Request Reference
          </Button>
        </div>

        {/* Reference List */}
        {references.length === 0 ? (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F5F8FF] flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-[#5B6472]" />
            </div>
            <h3 className="text-[#0A0F1E] font-semibold text-lg mb-2">No References Yet</h3>
            <p className="text-[#5B6472] mb-6 max-w-md mx-auto">
              Build your professional credibility by requesting references from people you've worked with.
            </p>
            <Button
              onClick={() => setShowModal(true)}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            >
              <Send className="w-4 h-4 mr-2" />
              Request Your First Reference
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {references.map((ref) => (
              <Card 
                key={ref.id}
                className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 hover:border-[#1B5AFF]/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                      <User className="w-6 h-6 text-[#5B6472]" />
                    </div>
                    <div>
                      <h3 className="text-[#0A0F1E] font-semibold">{ref.referee_name}</h3>
                      <p className="text-[#1B5AFF] text-sm">{ref.referee_relationship}</p>
                      <div className="flex items-center gap-4 mt-2 text-[#5B6472] text-sm">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {ref.referee_email}
                        </span>
                        {ref.referee_company && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {ref.referee_company}
                          </span>
                        )}
                      </div>
                      <p className="text-[#5B6472] text-xs mt-2">
                        Requested {formatDate(ref.created_at)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(ref.status)}
                    
                    {ref.status === 'pending' && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResend(ref.id)}
                          className="h-8 px-3 text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" />
                          Remind
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancel(ref.id)}
                          className="h-8 px-3 text-[#5B6472] hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                    
                    {ref.status === 'completed' && ref.response && (
                      <div className="mt-2 text-right">
                        <p className="text-amber-400 text-sm font-medium">
                          Rating: {ref.response.ratings?.average}/5
                        </p>
                        <p className="text-[#5B6472] text-xs">
                          {ref.response.would_work_again ? 'Would work again' : 'Would not work again'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {ref.status === 'completed' && ref.response?.strengths && (
                  <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
                    <p className="text-[#5B6472] text-sm">
                      <span className="text-[#0A0F1E] font-medium">Strengths: </span>
                      {ref.response.strengths}
                    </p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Request Reference Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Send className="w-5 h-5 text-[#1B5AFF]" />
              Request Reference
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Referee Name *</Label>
              <Input
                value={formData.referee_name}
                onChange={(e) => setFormData(prev => ({ ...prev, referee_name: e.target.value }))}
                placeholder="e.g., John Smith"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Referee Email *</Label>
              <Input
                type="email"
                value={formData.referee_email}
                onChange={(e) => setFormData(prev => ({ ...prev, referee_email: e.target.value }))}
                placeholder="john.smith@company.com"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Your Relationship *</Label>
              <Select 
                value={formData.referee_relationship} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, referee_relationship: v }))}
              >
                <SelectTrigger className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11">
                  <SelectValue placeholder="How do they know you?" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
                  {relationships.map(rel => (
                    <SelectItem key={rel} value={rel} className="text-[#0A0F1E] hover:bg-[#F5F8FF]">
                      {rel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Company (Optional)</Label>
              <Input
                value={formData.referee_company}
                onChange={(e) => setFormData(prev => ({ ...prev, referee_company: e.target.value }))}
                placeholder="Where you worked together"
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl h-11"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#0A0F1E]">Personal Message (Optional)</Label>
              <Textarea
                value={formData.custom_message}
                onChange={(e) => setFormData(prev => ({ ...prev, custom_message: e.target.value }))}
                placeholder="Add a personal note..."
                className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[80px]"
              />
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default References;
